import {Map as ImMap, List} from 'immutable';
import {values} from '@packages/utilities/key-map';
import {makeMintId, isDraft} from '@packages/systems/core/utils/RecordUtils';
import {get, flatten} from 'lodash';
import {COLLECTION_TYPES} from '@packages/systems/dynamo/constants';
import Field, {type FieldType} from '@packages/systems/dynamo/records/Field';
import type {CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';
import {
  ADVANCED_PRODUCT_TYPE,
  TEMPLATE_PRODUCT_TYPES,
  type ProductTypeType,
  type ProductTypeField,
} from '@packages/systems/commerce/constants';
import {
  ecommExtraFields,
  listableFieldTypes,
  dynamoCollectionDefaultFields,
} from '@packages/systems/core/constants/SharedConfig';
import {type EmbedTokenOption} from '@packages/systems/core/utils/EmbedUtils/types';
import {CollectionField} from '@packages/systems/dynamo/types';

const {CATEGORIES, CMS_COLLECTIONS, PRODUCTS, SKUS} = COLLECTION_TYPES;

type CollectionTypeKey =
  (typeof COLLECTION_TYPES)[keyof typeof COLLECTION_TYPES];

export const REFERENCE_FIELD_TYPES: ReadonlyArray<string> = [
  'ItemRef',
  'ItemRefSet',
];

export function mintField() {
  return ImMap({
    id: makeMintId(),
    required: false,
  }) as ImMap<string, any>;
}

export function isConfigurableField(field: FieldType) {
  return !NON_CONFIGURABLE_FIELD_SLUGS[field.slug];
}

export const isDefaultField = (
  field: FieldType,
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
): boolean => {
  return getDefaultFieldSlugs(collectionType).includes(field.get('slug'));
};

export function isItemRef(field: FieldType) {
  return REFERENCE_FIELD_TYPES.includes(field.get('type'));
}

export function isSingleRef(field: FieldType) {
  return field.get('type') === 'ItemRef';
}

export function isOption(field: FieldType) {
  return field.get('type') === 'Option';
}

export const isImageRef = (field: FieldType) =>
  field.get('type') === 'ImageRef';

export const isFileRef = (field: FieldType) => field.get('type') === 'FileRef';

export function isItemRefSet(field: FieldType) {
  return field.get('type') === 'ItemRefSet';
}

export function isRichText(field: FieldType) {
  return field.get('type') === 'RichText';
}

export function isImageRefSet(field: FieldType) {
  return isASetField(field) && field.get('innerType') === 'ImageRef';
}

export function isASetField(field: FieldType) {
  return field.get('type') === 'Set';
}

export function isSlugField(field: FieldType) {
  return field.get('slug') === 'slug';
}

export function isNameField(field: FieldType) {
  return field.get('slug') === 'name';
}

export function itemRefTargetCollectionId(field: FieldType): string | null {
  if (isItemRef(field)) {
    const collectionId = field.getIn(['validations', 'collectionId']);
    if (typeof collectionId === 'string') {
      return collectionId;
    }
  }
  return null;
}

function isSkuField(
  field: FieldType,
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
): boolean {
  if (collectionType === PRODUCTS) {
    return ecommExtraFields.sku.some((f) => f.slug === field.get('slug'));
  }
  return false;
}

export function isCustomField(
  field: FieldType,
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
) {
  return isConfigurableField(field) && isNoncustomField(field, collectionType);
}

export const isNoncustomField = (
  field: FieldType,
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
) => {
  return !NON_CUSTOM_FIELD_SLUGS[collectionType][field.slug];
};

const isListableFieldType = (type: string) => listableFieldTypes.includes(type);

const isListableFieldSlug = (slug: string) =>
  !['name', '_archived', '_draft', '_noSearch'].includes(slug);

const isListableField = (field: FieldType) =>
  isListableFieldSlug(field.get('slug')) &&
  isListableFieldType(field.get('type'));

export const listableFields = (fields: List<FieldType>) =>
  fields.filter(
    (field) => field.slug === '_noSearch' || isListableField(field)
  );

export function getConfigurableFields(fields: List<FieldType>) {
  return fields.filter((field) => isConfigurableField(field));
}

export function getCustomFields(
  fields: List<FieldType>,
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
) {
  return fields.filter((field) => isCustomField(field, collectionType));
}

export function getCustomCollectionFields(
  fields: CollectionField[],
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
): CollectionField[] {
  // @ts-expect-error - TS2769 - No overload matches this call.
  return fields.filter((field: FieldType) =>
    isCustomField(field, collectionType)
  );
}

export function getCustomFieldSlugs(
  fields: List<FieldType>,
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
): Array<string> {
  return (
    getCustomFields(fields, collectionType)
      // SKU fields on a product collection should not be considered custom
      .filter((f) => !isSkuField(f, collectionType))
      .map((f) => f.slug)
      .toArray()
  );
}

export function excludeFieldsWithSlugs(
  fields: List<FieldType>,
  fieldSlugs: ReadonlyArray<string | number>
) {
  const setOfSlugsToExclude = new Set(fieldSlugs);
  return fields.filter((field) => !setOfSlugsToExclude.has(field.get('slug')));
}

export function getFieldsWithSlugs(
  fields: List<FieldType>,
  fieldSlugs: ReadonlyArray<string | number>
): List<FieldType> {
  const mapOfSlugsToGetWithIndex = new Map(
    fieldSlugs.map((slug, index) => [slug, index])
  );

  return fields
    .filter((field) => mapOfSlugsToGetWithIndex.has(field.get('slug')))
    .sortBy((field) => mapOfSlugsToGetWithIndex.get(field.get('slug')));
}

/* Some field names need to be mapped to another name or phrase
 * Since slugs are more consistent and change less than names,
 * we map from slug to desired name which is used in `getFieldDisplayName`
 */
const DISPLAY_NAMES: Readonly<{[key: string]: string}> = {
  'compare-at-price': 'Compare-at price',
  'tax-category': 'Product tax class',
  shippable: 'Requires shipping',
  // the following need the second word to be lowercase
  'main-image': 'Main image',
  'more-images': 'More images',
  'track-inventory': 'Track inventory',
  'ec-sku-subscription-plan': 'Subscription plan',
};

export function getFieldDisplayName(field: FieldType): string {
  const slug = field.get('slug');

  if (field.get('name') === 'Variant image') {
    // the field has a 'main-image' slug, but we don't want the name to be DISPLAY_NAMES['main-image']
    return field.get('name');
  }

  return DISPLAY_NAMES[slug] || field.get('name');
}

export function getFieldDisplayType(field: FieldType) {
  return asFieldDisplayType(field.get('type'), field.get('innerType'));
}

export function asFieldDisplayType(type: string, innerType?: string) {
  const path = innerType ? [type, innerType] : [type];
  return get(DISPLAY_TYPES, path);
}

export function isRequired(field: FieldType) {
  return Boolean(field.get('required'));
}

export function isRequiredBasedOnProductType(field: FieldType, typeId: string) {
  const productTypeField = getProductTypeField(typeId, field.slug);

  return productTypeField ? productTypeField.required : isRequired(field);
}

export function isRequiredForItem(field: FieldType, item: Record<string, any>) {
  const neverOverride =
    field.get('slug') === 'name' || field.get('slug') === 'slug';
  if (isDraft(item)) {
    return neverOverride ? isRequired(field) : false;
  } else {
    return isRequired(field);
  }
}

const DISPLAY_TYPES = {
  Bool: 'Switch',
  Date: 'Date/Time',
  EnumOption: 'Option',
  HighlightedText: 'Plain text',
  ImageRef: 'Image',
  ItemRef: 'Reference',
  ItemRefSet: 'Multi-reference',
  Link: 'Link',
  Map: 'Map',
  Number: 'Number',
  PlainText: 'Plain text',
  RichText: 'Rich text',
  Video: 'Video link',
  Color: 'Color',
  Option: 'Option',
  Email: 'Email',
  Phone: 'Phone',
  Random: '',
  CommercePrice: 'Price',
  FileRef: 'File',
  Set: {
    ImageRef: 'Multi-image',
    ExtFileRef: 'File link',
  },
  SubscriptionPlan: 'Number',
  Password: 'Password',
} as const;

const convertFieldsToMap = (
  fieldSlugs: string[]
): {
  [key: string]: true;
} =>
  [...fieldSlugs]
    // For some reason these have to be sorted in a very specific way
    // in order for our tests to pass
    .sort((a, b) => a.localeCompare(b))
    .reduce((next, fieldSlug) => ({...next, [fieldSlug]: true}), {});

export const NON_CONFIGURABLE_FIELD_SLUGS: {
  [key: string]: true;
} = convertFieldsToMap([
  '_id',
  // Even though these are in the default fields,
  // The order in which they're inserted matters
  // as it relates to our cypress DOM snapshots
  // (we'd need to update every cypress DOM snapshot
  // if we didn't want to have to do this, which there is a lot).
  '_archived',
  '_draft',
  '_noSearch',
  ...dynamoCollectionDefaultFields
    .filter((field) => !field.editable)
    .map((field) => field.slug),
]);

/*
 * The way we determine which fields are custom fields for the CollectionFieldList
 * component is to gather all fields and filter out ones we _know_ aren't custom fields
 * or configurable fields. This enum allows for filtering per collection type
 */
export const NON_CUSTOM_FIELD_SLUGS: Readonly<{
  [key in keyof typeof COLLECTION_TYPES]: Readonly<{[fieldSlug: string]: true}>;
}> = {
  [CATEGORIES]: {
    name: true,
    slug: true,
    fullSlug: true,
  },
  [CMS_COLLECTIONS]: {
    name: true,
    slug: true,
    fullSlug: true,
  },
  [PRODUCTS]: convertFieldsToMap([
    'name',
    'slug',
    'fullSlug',
    'ecSkuInventoryQuantity',
    ...ecommExtraFields.product.map((field) => field.slug),
  ]),
  [SKUS]: {},
};

type DefaultFieldSlugsBySection = Readonly<{
  'Basic info'?: ReadonlyArray<string>;
  Media?: ReadonlyArray<string>;
  Billing?: ReadonlyArray<string>;
  Identifiers?: ReadonlyArray<string>;
  Inventory?: ReadonlyArray<string>;
  Shipping?: ReadonlyArray<string>;
  'Order items'?: ReadonlyArray<string>;
  Downloads?: ReadonlyArray<string>;
  Options?: ReadonlyArray<string>;
}>;

/*
 * This enum is used to derive a set of default Field records per section per collection type.
 * Each type is made up of an object with each key being a section title and each value an array
 * of strings that are matched against field.get('slug'). These array also represent the order that
 * we want these fields to show up in the CollectionFieldList component.
 */
export const DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION: Readonly<{
  [key in CollectionTypeKey]: DefaultFieldSlugsBySection;
}> = {
  [CATEGORIES]: {'Basic info': ['name', 'slug', 'product']},
  [CMS_COLLECTIONS]: {'Basic info': ['name', 'slug']},
  [PRODUCTS]: {
    'Basic info': [
      'ec-product-type',
      'name',
      'slug',
      'description',
      'category',
    ],
    Media: ['main-image', 'more-images'],
    Billing: [
      'ec-sku-billing-method',
      'ec-sku-subscription-plan',
      'price',
      'compare-at-price',
      'tax-category',
    ],
    Identifiers: ['sku'],
    Inventory: ['track-inventory', 'quantity', 'ecSkuInventoryQuantity'],
    Shipping: ['shippable', 'weight', 'width', 'height', 'length'],
    'Order items': ['count', 'rowTotal'],
    Downloads: ['include-downloads', 'download-files'],
    Options: ['options'],
  },
  [SKUS]: {},
};

export const HARD_CODED_FIELDS_PER_COLLECTION_TYPE = {
  /* The relationship between products and categories is one way,
   * and thus cannot be derived from the fields on a category.
   * We hardcode the Products field, for that reason.
   */
  [CATEGORIES]: [
    Field({
      id: 'product',
      name: 'Products',
      slug: 'product',
      type: 'ItemRefSet',
    }),
  ],
  [CMS_COLLECTIONS]: [],
  /* The correct fields for Products involve a composition of
   * product fields, SKU fields, and some hardcoded "fields".
   * Documentation for this can be found here:
   * https://github.com/webflow/webflow/blob/dev/docs/product/ecommerce/e-commerce-collections.md
   */
  [PRODUCTS]: [
    Field({
      id: 'track-inventory',
      name: 'Track inventory',
      slug: 'track-inventory',
      type: 'Bool',
    }),
    Field({
      id: 'quantity',
      name: 'Quantity',
      slug: 'quantity',
      type: 'Number',
    }),
    Field({
      id: 'include-downloads',
      name: 'Include download',
      slug: 'include-downloads',
      type: 'Bool',
    }),
    Field({
      id: 'options',
      name: 'Options',
      slug: 'options',
      type: 'ItemRefSet',
    }),
  ],
  [SKUS]: [],
} as const;

/*
 * These are all the slugs for product fields that are used on a variant form.
 */

export const DEFAULT_FIELD_SLUGS_FOR_PRODUCT_COLLECTION_VARIANTS = [
  'main-image', // appears as `Variant image`
  'more-images',
  'track-inventory',
  'quantity',
  'sku',
  'ec-sku-billing-method',
  'ec-sku-subscription-plan',
  'price',
  'compare-at-price',
  'weight',
  'width',
  'height',
  'length',
  'include-downloads',
  'download-files',
] as const;

export const DEFAULT_SKU_FIELD_SLUGS_TO_SAMPLE: Set<string> = new Set([
  'main-image',
  'price',
  'compare-at-price',
  'width',
  'height',
  'length',
  'weight',
]);

export const getDefaultFieldSlugs = (
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
): Array<string> =>
  flatten(
    values(DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION[collectionType] || {})
  );

export const getBasicInfoFieldSlugs = (
  collectionType: CollectionTypeKey = CMS_COLLECTIONS
): ReadonlyArray<string> =>
  DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION[collectionType][
    'Basic info'
  ] || [];

const isDefaultProductSlug = (slug: string): boolean =>
  getDefaultFieldSlugs(PRODUCTS).includes(slug);

export const getProductSlugSortValue = (slug: string): number => {
  // sort slugs based on where the field is on the product form
  const defaultProductSlugs = getDefaultFieldSlugs(PRODUCTS);
  const downloadFilesIndex = defaultProductSlugs.findIndex(
    (defaultSlug) => defaultSlug === 'download-files'
  );
  // we want to sort the download file name before the url
  defaultProductSlugs.splice(
    downloadFilesIndex,
    0,
    'download-files.name',
    'download-files.url'
  );

  return slug.includes('quantity')
    ? // quantity slug is different on variant and product forms (i.e. `inventory.quantity` vs. `product-quantity`)
      defaultProductSlugs.findIndex((defaultSlug) => slug.includes(defaultSlug))
    : defaultProductSlugs.findIndex((defaultSlug) => slug === defaultSlug);
};

export const DEFAULT_DATETIME_FIELD_SLUGS = [
  'created-on',
  'updated-on',
  'published-on',
];

export const DEFAULT_USER_DATETIME_FIELD_SLUGS = [
  'created-by',
  'updated-by',
  'published-by',
];

const STATUS_FIELD_SLUGS = ['_draft', '_archived'];

const getDateTimeSlugSortValue = (slug: string): number =>
  DEFAULT_DATETIME_FIELD_SLUGS.findIndex((defaultSlug) => defaultSlug === slug);

// sorts items by section based on where they are on the product form
export const sortByProductSection = <ItemType>(
  items: Array<ItemType>,
  getSlug: (arg1: ItemType) => {
    slug: string;
    fromCustomReferenceField: boolean;
  }
): {[section: string]: ItemType[]} => {
  const productFieldSlugsBySection =
    DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION[PRODUCTS];
  const sortedItems: Record<string, ItemType[]> = {};
  const initialItemsBySection = [
    ...Object.keys(productFieldSlugsBySection),
    'Custom fields',
    'Other',
  ].reduce<Record<string, ItemType[]>>((itemsBySection, section) => {
    itemsBySection[section] = [];
    return itemsBySection;
  }, {});
  const itemsBySection = items.reduce((sections, item) => {
    const {slug, fromCustomReferenceField} = getSlug(item);

    if (fromCustomReferenceField) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      sections['Custom fields'].push(item);
      return sections;
    }

    if (isDefaultProductSlug(slug) || slug === 'requires-shipping') {
      for (const section in productFieldSlugsBySection) {
        if (
          productFieldSlugsBySection[
            section as keyof DefaultFieldSlugsBySection
          ]?.includes(slug) ||
          (section === 'Shipping' && slug === 'requires-shipping')
        ) {
          // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
          sections[section].push(item);
          break;
        }
      }
    } else if (
      [
        'categories',
        'product',
        'default-sku',
        ...DEFAULT_DATETIME_FIELD_SLUGS,
      ].includes(slug) ||
      // refers to the cart line item Quantity
      (slug === 'count' &&
        ((Array.isArray(item) && !get(item, [`${item.length - 1}`, 'id'])) ||
          (typeof item === 'object' &&
            item !== null &&
            'name' in item &&
            item.name === 'Quantity'))) ||
      // and to the Total Price for the cart line item
      slug === 'rowTotal' ||
      get(item, 'value') === 'rowTotal'
    ) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      sections['Other'].push(item);
    } else {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      sections['Custom fields'].push(item);
    }
    return sections;
  }, initialItemsBySection);

  for (const section in itemsBySection) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    if (itemsBySection[section].length) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      sortedItems[section] = itemsBySection[section].sort((lhs, rhs) => {
        const lhSlug = getSlug(lhs).slug;
        const rhSlug = getSlug(rhs).slug;
        const defaultProductSection =
          productFieldSlugsBySection[
            section as keyof DefaultFieldSlugsBySection
          ];
        if (defaultProductSection) {
          return (
            getProductSlugSortValue(lhSlug) - getProductSlugSortValue(rhSlug)
          );
        }
        if (section === 'Other') {
          return (
            getDateTimeSlugSortValue(lhSlug) - getDateTimeSlugSortValue(rhSlug)
          );
        }
        return 0;
      });
    }
  }

  return sortedItems;
};

export const sortTokensByProductSection = (
  tokens: EmbedTokenOption[]
): EmbedTokenOption[] => {
  return flatten(
    values(
      sortByProductSection<EmbedTokenOption>(
        tokens,
        (
          option: EmbedTokenOption
        ): {
          slug: string;
          fromCustomReferenceField: boolean;
        } => {
          const optionValueAsJSON = option.value || '{}';
          const path = (JSON.parse(optionValueAsJSON).path || '').split(':');
          const slug = path[path.length - 1];
          const fromCustomReferenceField =
            path.length > 1 && !DEFAULT_REFERENCE_FIELD_SLUGS.includes(path[0]);
          return {slug, fromCustomReferenceField};
        }
      )
    )
  );
};

export const DEFAULT_REFERENCE_FIELD_SLUGS = [
  'default-sku',
  'product',
  'sku',
] as const;

export const isAdvancedProductType = (id: string): boolean =>
  id === ADVANCED_PRODUCT_TYPE.id;

export const getProductType = (
  typeId: string
): ProductTypeType | null | undefined =>
  TEMPLATE_PRODUCT_TYPES.find((type) => type.id === typeId);

export const getProductTypeId = (
  item: ImMap<string, any>,
  defaultId: string = ADVANCED_PRODUCT_TYPE.id
): string => (item && item.get('ec-product-type')) || defaultId;

export const getProductTypeName = (
  item: ImMap<string, any>,
  defaultName: string = ''
): ProductTypeType['name'] => {
  const productTypeId = getProductTypeId(item, ''); // we don't want a valid defaultId
  const productType = getProductType(productTypeId);

  return productType ? productType.name : defaultName;
};

const getProductTypeFields = (
  productType: ProductTypeType
): Array<ProductTypeField> => flatten(values(productType.fields || {}));

export const getProductTypeField = (
  typeId: string,
  slug: string
): ProductTypeField | null | undefined => {
  const productType = getProductType(typeId);
  return (
    productType &&
    getProductTypeFields(productType).find((f) => f.fieldSlug === slug)
  );
};

export const getProductTypeFieldSlugs = (
  productType: ProductTypeType
): Array<string> => getProductTypeFields(productType).map((f) => f.fieldSlug);

export const dateAndStatusFieldSlugs = new Set([
  ...STATUS_FIELD_SLUGS,
  ...DEFAULT_USER_DATETIME_FIELD_SLUGS,
  ...DEFAULT_DATETIME_FIELD_SLUGS,
]) as Set<string>;

export const getAdvancedProductTypeFieldSlugs = (
  collection: Collection
): string[] =>
  collection.fields.reduce<string[]>((fieldSlugs, field) => {
    const slug = field.get('slug');
    if (!dateAndStatusFieldSlugs.has(slug)) {
      fieldSlugs.push(slug);
    }
    return fieldSlugs;
  }, []);

export const hasDefaultValue = (fieldValue: any): boolean =>
  fieldValue === null ||
  // switch field (i.e.`shippable`) is already set to false
  fieldValue === false ||
  // custom fields that haven't had values entered will not be on the item
  fieldValue === undefined ||
  // some field default values are empty Lists or arrays (i.e. `download-files` or `more-images`)
  fieldValue.size === 0 ||
  fieldValue.length === 0;

// count how many reference fields a collection has
export const countReferenceFields = (fields: List<FieldType>): number =>
  fields.count(isItemRef);

export const isDateTimeField = (field: FieldType): boolean => {
  if (field.type !== 'Date') {
    return false;
  }
  return (
    field.getIn(['validations', 'format']) === 'date-time' ||
    DEFAULT_DATETIME_FIELD_SLUGS.includes(field.slug)
  );
};

export const canFieldDataLoadingBeDeferred = (field: FieldType): boolean => {
  const deferrableFieldMatchers = [isRichText, isImageRefSet];

  return deferrableFieldMatchers.some((match) => match(field));
};
